/**
 * BottomSheet — the phone's slide-up surface over the current page.
 *
 * A thin shape on top of `Modal` (which owns the portal, focus trap, Escape
 * stack, scroll lock and scrim): a panel anchored to the bottom edge, a grab
 * handle that closes on a swipe down, an optional visible title, and a body
 * that scrolls inside a height cap so the page underneath is still there when
 * the sheet closes. Used for More on the bottom bar and the mail view picker.
 */
import React, { useRef } from 'react';

import { Modal } from './Modal';

/** Vertical travel (px) on the handle that counts as "swipe down to close". */
const SWIPE_CLOSE_PX = 60;

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** The dialog's accessible name, also rendered as its heading. */
  title: string;
  children: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({ isOpen, onClose, title, children }) => {
  const touchStartY = useRef<number | null>(null);

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="bare" size="fullscreen" ariaLabel={title}>
      <div
        data-testid="bottom-sheet"
        className="bg-card text-foreground pb-safe fixed inset-x-0 bottom-0 flex max-h-[80vh] flex-col rounded-t-xl shadow-lg"
      >
        <div
          data-testid="bottom-sheet-handle"
          className="flex shrink-0 justify-center py-3"
          onTouchStart={e => {
            touchStartY.current = e.touches[0]?.clientY ?? null;
          }}
          onTouchEnd={e => {
            const start = touchStartY.current;
            touchStartY.current = null;
            const end = e.changedTouches[0]?.clientY;
            if (start != null && end != null && end - start > SWIPE_CLOSE_PX) onClose();
          }}
        >
          <div className="bg-border h-1 w-10 rounded-full" />
        </div>
        <h2 className="text-muted-foreground shrink-0 px-4 pb-2 text-xs font-medium tracking-wide uppercase">
          {title}
        </h2>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </Modal>
  );
};
